import React, { useState, useCallback, useEffect } from 'react';
import { Box, Flex, Image, Fade, Grid, Button } from '@chakra-ui/react';
import routes from 'constants/routes';
import { selectUser } from 'store/slices/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setDisplayingStatus } from 'store/slices/paywallSlice';

const Slider = ({ autoplayInterval = 5000, onButtonClick, onLinkClick }) => {
  const user = useSelector(selectUser);
  const initialIndex = useSelector(state => state.paywall.initialIndex) || 0;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentSlide, setCurrentSlide] = useState(initialIndex);
  const [inTransition, setInTransition] = useState(false);

  const slides = [
    {
      id: 1,
      title: (
        <>
          GET 50 COINS
          <br />
          PER MONTH
        </>
      ),
      description: 'EARN & TRADE FOR MERCH',
      imageUrl: 'images/paywall/hoodie-cap.webp',
      backgroundImage: 'url(images/paywall/bg-2.webp)',
      buttonText: 'Join Now',
      buttonAction: () => alert('Join Now clicked!'),
    },
    {
      id: 2,
      title: (
        <>
          USE 10 SWAPS
          <br />
          PER DAY
        </>
      ),
      description: 'BUILD ELITE HANDS',
      imageUrl: 'images/paywall/swap.webp',
      backgroundImage: 'url(images/paywall/bg-1.webp)',
      buttonText: 'Join Now',
      buttonAction: () => alert('Join Now clicked!'),
    },
    {
      id: 3,
      title: (
        <>
          ACCESS
          <br />
          WEEKLY RAFFLES
        </>
      ),
      description: (
        <>
          Win collectibles weekly.{' '}
          <Box
            as="span"
            onClick={() => {
              navigate(routes.raffleRules);
              dispatch(setDisplayingStatus());
            }}
            to={routes.raffleRules}
            color="var(--chakra-colors-blue)"
          >
            RULES {'>'}
          </Box>
        </>
      ),
      imageUrl: null,
      backgroundImage: 'images/raffle/prizes_full.webp',
      buttonText: 'Join Now',
      buttonAction: () => alert('Join Now clicked!'),
    },
  ];

  const onBulletClick = index => {
    changeSlide(index);
  };

  const changeSlide = useCallback(index => {
    setInTransition(true);
    setTimeout(() => {
      if (index !== undefined) {
        setCurrentSlide(index);
      } else {
        setCurrentSlide(prev => (prev + 1) % slides.length);
      }
      setInTransition(false);
    }, 500);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      changeSlide();
    }, autoplayInterval);

    return () => clearInterval(interval);
  }, [autoplayInterval]);

  return (
    <Flex position="relative" width="100%" flexDirection="column" gap="0" height="100%">
      <Box overflow="hidden" position="relative" borderRadius="lg" mb="-115px">
        <Fade
          in={!inTransition}
          transition={{ enter: { duration: 0.3 }, exit: { duration: 0.3 } }}
          style={{ padding: 0 }}
        >
          {slides.map((slide, index) => {
            return (
              <Box
                key={slide.id}
                width="100%"
                aspectRatio="1/1"
                transition="opacity 0.5s ease-in-out, transform 0.5s ease-in-out"
                backgroundImage={slide.backgroundImage}
                backgroundSize={slide.imageUrl ? 'contain' : '290px'}
                backgroundPosition="center"
                backgroundRepeat="no-repeat"
                textAlign="center"
                display={index === currentSlide ? 'block' : 'none'}
                position="relative"
              >
                {!slide.imageUrl && (
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    backgroundImage="radial-gradient(circle at 50% 50%, rgba(22,31,40,0) 0%, rgba(22,31,40,0.5) 40%)"
                  />
                )}
                <Flex height="100%" direction="column" align="center" gap="8px">
                  <Box
                    fontSize="28px"
                    fontWeight="400"
                    color="#fff"
                    fontFamily="Fugaz One"
                    textAlign="center"
                    lineHeight="1.1"
                    textTransform="uppercase"
                    zIndex="1"
                  >
                    {slide.title}
                  </Box>
                  <Box
                    fontSize="14px"
                    fontWeight="600"
                    color="#fff"
                    textAlign="center"
                    lineHeight="1.4"
                    textTransform="uppercase"
                    zIndex="1"
                  >
                    {slide.description}
                  </Box>
                  <Flex
                    position="absolute"
                    h="100%"
                    w="100%"
                    mx="auto"
                    textAlign="center"
                    justifyContent="center"
                    alignItems="center"
                  >
                    {slide.imageUrl && <Image src={slide.imageUrl} alt={slide.title} h="200px" objectFit="contain" />}
                  </Flex>
                </Flex>
              </Box>
            );
          })}
        </Fade>
      </Box>

      <Flex justifyContent="center">
        <Flex
          alignItems="center"
          justifyContent="center"
          backgroundColor="rgba(255,255,255,0.06)"
          p="4px"
          gap="8px"
          borderRadius="full"
          zIndex="1"
        >
          {slides.map((_, index) => (
            <Box
              key={index}
              as="button"
              onClick={() => onBulletClick(index)}
              w="8px"
              h="8px"
              borderRadius="full"
              bg={index === currentSlide ? 'var(--chakra-colors-blue)' : '#788DA2'}
              transition="all 0.4s"
              _active={{ transform: 'scale(1.2)' }}
            />
          ))}
        </Flex>
      </Flex>

      <Flex
        flexDirection="column"
        alignItems="center"
        gap="0"
        pos="relative"
        flex="1"
        textAlign="center"
        px="20px"
        fontSize="13px"
        fontWeight="700"
      >
        <Grid gridTemplateColumns="1fr 65px 77px" gap="0" w="100%">
          <Box></Box>
          <Box fontFamily="Fugaz One" textAlign="center" lineHeight="1.1" textTransform="uppercase" py="10px">
            FREE
          </Box>
          <Box
            fontFamily="Fugaz One"
            textAlign="center"
            lineHeight="1.1"
            textTransform="uppercase"
            backgroundColor="#172B3E"
            borderRadius="12px 12px 0 0"
            py="10px"
          >
            PREMIUM
          </Box>
        </Grid>
        <Grid gridTemplateColumns="1fr 65px 77px" gap="0" w="100%">
          <Box py="10px" textAlign="left">
            SWAPS / DAY
          </Box>
          <Box py="10px">2</Box>
          <Box backgroundColor="#172B3E" py="10px">
            10
          </Box>
        </Grid>
        <Grid gridTemplateColumns="1fr 65px 77px" gap="0" w="100%">
          <Box py="10px" textAlign="left">
            BONUS COINS / MONTH
          </Box>
          <Box py="10px">-</Box>
          <Box backgroundColor="#172B3E" py="10px">
            50
          </Box>
        </Grid>
        <Grid gridTemplateColumns="1fr 65px 77px" gap="0" w="100%">
          <Box py="10px" textAlign="left">
            WEEKLY RAFFLE ACCESS
          </Box>
          <Box py="10px">-</Box>
          <Box backgroundColor="#172B3E" borderRadius="0 0 12px 12px" py="10px">
            +
          </Box>
        </Grid>
      </Flex>
      <Flex flex="1" justifyContent="center" mt="14px" w="100%" flexDirection="column" alignItems="center" gap="8px">
        <Button
          variant="blue"
          onClick={() => {
            if (!user?.subscribed) {
              onButtonClick();
            }
          }}
          size="lg"
          fontSize="16px"
          fontWeight="400"
          color="#fff"
          fontFamily="Fugaz One"
          textAlign="center"
          lineHeight="1"
          textTransform="uppercase"
          minW="250px"
          position="relative"
          overflow="hidden"
          isDisabled={user?.subscribed}
          _before={
            user?.subscribed
              ? {}
              : {
                  content: '""',
                  position: 'absolute',
                  top: '-100%',
                  left: '-100%',
                  width: '30px',
                  height: '300%',
                  background: 'rgba(255,255,255,0.2)',
                  transform: 'rotate(-25deg)',
                  animation: 'shimmer 1.5s 1s infinite ',
                }
          }
          sx={{
            '@keyframes shimmer': {
              '0%': { left: '-100px' },
              '100%': { left: 'calc(100% + 50px)' },
            },
          }}
        >
          {user?.subscribed ? 'SUBSCRIBED' : 'UPGRADE'}
        </Button>
        <Box color="rgba(255,255,255,0.6)" fontSize="12px" fontWeight="600">
          $9.99/mo. Auto-renewable. Cancel anytime.
        </Box>
      </Flex>
    </Flex>
  );
};

export default Slider;
